import type { ConditionGroup, SectionDef, TemplateDefinition } from '../forms/types.js';
import { ACCESS_STATUS_OPTIONS, NO_PASSWORDS_NOTICE, SectionBuilder } from './builder.js';
import { buildC01Section, C01_CONFIRMATION } from './c01.js';

/** F02 — Google Business Profile Onboarding (11 numbered sections + C01). */

const has = (field: string, values: string[]): ConditionGroup => ({ all: [{ field, op: 'contains_any', value: values }] });
const isTrue = (field: string): ConditionGroup => ({ all: [{ field, op: 'equals', value: true }] });

function sec(n: number, key: string, title: string) {
  const nn = String(n).padStart(2, '0');
  return new SectionBuilder(`f02_s${nn}`, { number: `F02.${nn}`, key, title });
}

function assetItem(sb: SectionBuilder, key: string, label: string, category: string) {
  sb.add(`${key}_provided`, 'yes_no', label, { assetCategory: category, help: 'Provided? (Yes/No)' });
  const vis = isTrue(sb.id(`${key}_provided`));
  sb.add(`${key}_file`, 'file_upload', `${label}: File`, { assetCategory: category, visibleWhen: vis });
  sb.add(`${key}_folder`, 'asset_link', `${label}: Folder`, { assetCategory: category, visibleWhen: vis });
}

function s01(): SectionDef {
  const sb = sec(1, 'business_information', 'Business Information');
  sb.add('legal_business_name', 'short_text', 'Legal Business Name', { required: true, profileKey: 'legal_name' });
  sb.add('public_trading_name', 'short_text', 'Public/Trading Name', { required: true, profileKey: 'public_brand' });
  sb.add('primary_contact', 'short_text', 'Primary Contact', { required: true, profileKey: 'primary_contact_name' });
  sb.add('designation', 'short_text', 'Designation', { profileKey: 'primary_contact_designation' });
  sb.add('official_email', 'email', 'Official Email', { required: true, profileKey: 'email' });
  sb.add('phone', 'phone', 'Phone', { required: true, profileKey: 'phone' });
  sb.add('website', 'url', 'Website', { profileKey: 'website' });
  sb.add('opening_date', 'date', 'Opening Date');
  sb.add('business_description', 'long_text', 'Business Description', { required: true, profileKey: 'business_description' });
  return sb.build();
}

function s02(): SectionDef {
  const sb = sec(2, 'category_location', 'Categories, Business Type & Location');
  sb.add('primary_category', 'short_text', 'Primary Category', { required: true });
  sb.add('secondary_categories', 'long_text', 'Secondary Categories');
  sb.add('business_type', 'single_select', 'Business type', {
    required: true,
    options: ['Storefront', { label: 'Service-area', key: 'service_area' }, 'Hybrid'],
  });
  const opt = (k: string) => sb.optionId('business_type', k);
  const hasAddress = has(sb.id('business_type'), [opt('storefront'), opt('hybrid')]);
  const hasAreas = has(sb.id('business_type'), [opt('service_area'), opt('hybrid')]);
  sb.add('business_address', 'long_text', 'Business Address', { visibleWhen: hasAddress, requiredWhen: hasAddress });
  sb.add('city_state_country', 'short_text', 'City/State/Country', { required: true });
  sb.add('postal_code', 'short_text', 'Postal Code', { visibleWhen: hasAddress, requiredWhen: hasAddress });
  sb.add('service_areas', 'long_text', 'Service Areas', { visibleWhen: hasAreas, requiredWhen: hasAreas });
  sb.add('services_products', 'long_text', 'Services/products');
  return sb.build();
}

function s03(): SectionDef {
  const sb = sec(3, 'hours', 'Business Hours');
  sb.add('regular_hours', 'weekly_hours', 'Regular Hours (Monday–Sunday)', {
    required: true,
    help: 'Enter Monday–Sunday regular hours; mark days the business is closed as Closed.',
  });
  sb.add('special_hours', 'long_text', 'Special/holiday hours');
  sb.info(
    'locations_note',
    'Each additional location is treated as its own engagement location. Add one row per location with its hours.',
    'Multiple locations',
  );
  const r = 'additional_locations';
  sb.add(r, 'repeater', 'Additional Locations', {
    rowLabel: 'Location',
    validation: { maxRows: 100 },
    children: [
      sb.child(r, 'location_name', 'short_text', 'Location name', { required: true }),
      sb.child(r, 'location_hours', 'weekly_hours', 'Location hours'),
      sb.child(r, 'location_notes', 'long_text', 'Location notes'),
    ],
  });
  return sb.build();
}

function s04(): SectionDef {
  const sb = sec(4, 'attributes', 'Attributes & Amenities');
  sb.add('attributes', 'multi_select', 'Attributes', {
    options: ['Accessibility', 'Appointments', 'Online appointments', 'Delivery', 'Pickup', { label: 'Other', key: 'other', opensDetail: true }],
  });
  sb.add('attributes_amenities', 'long_text', 'Attributes/amenities');
  return sb.build();
}

function s05(): SectionDef {
  const sb = sec(5, 'existing_profile', 'Existing Profile');
  sb.add('profile_status', 'multi_select', 'Existing profile status', {
    required: true,
    options: [
      { label: 'No existing', key: 'no_existing', exclusive: true },
      'Existing',
      { label: 'Duplicate/multiple', key: 'duplicate_multiple' },
      { label: 'Not sure', key: 'not_sure', exclusive: true },
    ],
  });
  const exists = has(sb.id('profile_status'), [sb.optionId('profile_status', 'existing'), sb.optionId('profile_status', 'duplicate_multiple')]);
  sb.add('profile_url', 'url', 'Profile URL', { visibleWhen: exists, help: 'For duplicates, list the additional URLs under Issues.' });
  sb.add('current_details', 'long_text', 'Current name/address/category', { visibleWhen: exists });
  sb.add('profile_issues', 'long_text', 'Issues', { visibleWhen: exists });
  sb.add('suspensions', 'long_text', 'Suspensions', { visibleWhen: exists });
  sb.add('verification_history', 'long_text', 'Verification history', { visibleWhen: exists });
  return sb.build();
}

function s06(): SectionDef {
  const sb = sec(6, 'ownership_verification', 'Ownership, Access & Verification');
  sb.info('no_passwords', NO_PASSWORDS_NOTICE, 'NEVER collect passwords, recovery codes or OTPs');
  sb.add('google_account_email', 'email', 'Google account/email', { accessProvider: 'gbp' });
  sb.add('current_owner_manager', 'short_text', 'Current owner/manager', { accessProvider: 'gbp' });
  sb.add('ownership_status', 'single_select', 'Ownership status', {
    required: true,
    accessProvider: 'gbp',
    options: ['Verified owner', 'Needs to claim', 'Access needs added', 'New profile setup'],
  });
  sb.add('access_status', 'single_select', 'Access status', {
    accessProvider: 'gbp',
    options: ACCESS_STATUS_OPTIONS,
    help: 'Pending/Granted — whether the requested user has been added with the appropriate role.',
  });
  sb.add('access_role', 'short_text', 'Appropriate role', { accessProvider: 'gbp', placeholder: 'Owner or Manager' });
  sb.add('verification_status', 'single_select', 'Verification status', {
    options: ['Verified', 'Unverified', 'Pending verification', 'Suspended', 'Not sure'],
  });
  sb.add('verification_method', 'single_select', 'Verification method', {
    options: ['Postcard', 'Phone', 'Email', 'Video', 'Instant', 'Not sure', { label: 'Other', key: 'other', opensDetail: true }],
  });
  sb.add('supporting_docs', 'file_upload', 'Supporting docs', {
    sensitivity: 'restricted',
    assetCategory: 'verification_documents',
    validation: { accept: ['document', 'image'] },
  });
  return sb.build();
}

function s07(): SectionDef {
  const sb = sec(7, 'address_verification', 'Address & Premises');
  sb.add('address_display', 'single_select', 'Public address vs Service area only', {
    required: true,
    options: [
      { label: 'Public address', key: 'public_address' },
      { label: 'Service area only', key: 'service_area_only' },
    ],
  });
  const pub: ConditionGroup = { all: [{ field: sb.id('address_display'), op: 'equals', value: sb.optionId('address_display', 'public_address') }] };
  const priv: ConditionGroup = { all: [{ field: sb.id('address_display'), op: 'equals', value: sb.optionId('address_display', 'service_area_only') }] };
  sb.add('customer_facing_address', 'long_text', 'Exact customer-facing address', { visibleWhen: pub, requiredWhen: pub });
  sb.add('exterior_signage', 'yes_no', 'Exterior signage', { visibleWhen: pub });
  sb.add('interior_signage', 'yes_no', 'Interior signage', { visibleWhen: pub });
  sb.add('street_facing_entrance', 'yes_no', 'Street-facing entrance', { visibleWhen: pub });
  sb.add('private_address_note', 'info', 'Service area only', {
    visibleWhen: priv,
    text: 'The address will be hidden from the public profile. A private address may still be needed for verification and is treated as confidential.',
  });
  sb.add('private_address', 'long_text', 'Verification address (hidden from public profile)', {
    visibleWhen: priv,
    sensitivity: 'confidential',
  });
  sb.add('proof_folder', 'asset_link', 'Proof folder', { assetCategory: 'address_proof', sensitivity: 'confidential' });
  return sb.build();
}

function s08(): SectionDef {
  const sb = sec(8, 'photos_media', 'Photos & Media');
  assetItem(sb, 'logo', 'Logo', 'logo');
  assetItem(sb, 'cover', 'Cover image', 'cover_image');
  assetItem(sb, 'exterior', 'Exterior', 'exterior_photo');
  assetItem(sb, 'interior', 'Interior', 'interior_photo');
  assetItem(sb, 'team', 'Team', 'team_photo');
  assetItem(sb, 'products', 'Products/Services', 'product_photo');
  assetItem(sb, 'video', 'Video', 'video');
  sb.add('location_specific', 'yes_no', 'Assets are location-specific');
  return sb.build();
}

function s09(): SectionDef {
  const sb = sec(9, 'services_products_offers', 'Services, Products & Offers');
  const rs = 'priority_services';
  sb.add(rs, 'repeater', 'Priority services', {
    rowLabel: 'Service',
    validation: { maxRows: 100 },
    children: [
      sb.child(rs, 'service', 'short_text', 'Service', { required: true }),
      sb.child(rs, 'description', 'long_text', 'Description'),
      sb.child(rs, 'price', 'currency', 'Price', { validation: { min: 0 } }),
      sb.child(rs, 'booking_link', 'url', 'Booking link'),
    ],
  });
  const rp = 'priority_products';
  sb.add(rp, 'repeater', 'Priority products', {
    rowLabel: 'Product',
    validation: { maxRows: 100 },
    children: [
      sb.child(rp, 'product', 'short_text', 'Product', { required: true }),
      sb.child(rp, 'description', 'long_text', 'Description'),
      sb.child(rp, 'price', 'currency', 'Price', { validation: { min: 0 } }),
    ],
  });
  sb.add('offers', 'long_text', 'Offers');
  sb.add('promos', 'long_text', 'Promos');
  sb.add('booking', 'url', 'Booking', { help: 'Main booking/appointment link, if any.' });
  return sb.build();
}

function s10(): SectionDef {
  const sb = sec(10, 'reviews', 'Reviews & Reputation');
  sb.add('current_rating', 'decimal', 'Current rating', { validation: { min: 0, max: 5 } });
  sb.add('review_count', 'integer', 'Review count', { validation: { min: 0 } });
  sb.add('reviews_as_of', 'date', 'As-of date', { dateConstraint: 'past' });
  sb.add('review_response_management', 'single_select', 'Review response management', {
    options: ['Client', { label: 'Agency/team', key: 'agency_team' }, 'Shared', 'Not currently managed'],
  });
  sb.add('review_generation_strategy', 'long_text', 'Review generation strategy');
  sb.add('review_monitoring', 'long_text', 'Monitoring');
  sb.add('response_guidelines', 'long_text', 'Response guidelines');
  return sb.build();
}

function s11(): SectionDef {
  const sb = sec(11, 'compliance_documents', 'Registration & Compliance');
  sb.add('registration_license', 'long_text', 'Registration/license where relevant', { sensitivity: 'confidential' });
  sb.add('documents', 'file_upload', 'Documents/links', {
    sensitivity: 'restricted',
    assetCategory: 'legal_documents',
    validation: { accept: ['document', 'image'] },
  });
  sb.add('documents_link', 'asset_link', 'Documents link', { sensitivity: 'restricted', assetCategory: 'legal_documents' });
  sb.add('compliance_notes', 'long_text', 'Notes');
  return sb.build();
}

export const F02_GBP: TemplateDefinition = {
  key: 'gbp_onboarding',
  code: 'F02',
  name: 'Google Business Profile Onboarding',
  description: 'Onboarding questionnaire for Google Business Profile setup and management.',
  serviceModule: 'gbp',
  sections: [s01(), s02(), s03(), s04(), s05(), s06(), s07(), s08(), s09(), s10(), s11(), buildC01Section('f02')],
  confirmation: C01_CONFIRMATION,
};
